// presence-schema.ts
import { encode, decode } from "@msgpack/msgpack";
import z from "zod";

export type Cursor = {
  x: number;
  y: number;
  pointer: "mouse" | "touch";
};

// user-modifiable fields
export type Presence = {
  verifiedDomain?: string | null; // Keep as optional for initial state
  name?: string; // Made optional
  color?: string; // Made optional
  cursor?: Cursor | null;
  message?: string | null;
  cursorIndex?: number;
  spotlightColor?: string;
};

// set on the server, read-only for the duration of the session
export type Metadata = {
  country: string | null;
};

// additional fields that are set by the server
// and do not change for the duration of the session
export type User = {
  presence: Presence;
  // Add persistent fields that don't change during session
  // verifiedDomain?: string | null; // Removed from User level as it's now consistently in Presence
};

export type PartyMessage =
  | {
      type: "sync";
      users: { [id: string]: User };
    }
  | {
      type: "changes";
      add?: { [id: string]: User };
      presence?: { [id: string]: Presence };
      remove?: string[];
    };

export type ClientMessage = {
  type: "update";
  presence: Presence; // Changed from Omit<Presence, 'verifiedDomain'> to Presence
};

// Schema created with https://transform.tools/typescript-to-zod
// and then z.union -> z.discriminatedUnion with an additional "type" as first arg

export const cursorSchema = z.object({
  x: z.number(),
  y: z.number(),
  pointer: z.union([z.literal("mouse"), z.literal("touch")]),
});

export const presenceSchema = z.object({
  verifiedDomain: z.string().optional().nullable(), // Keep in presence schema
  name: z.string().optional(),
  color: z.string().optional(),
  cursor: cursorSchema.optional().nullable(),
  message: z.string().optional().nullable(),
  cursorIndex: z.number().optional(),
  spotlightColor: z.string().optional(),
});

// Schema for client updates (now includes verifiedDomain)
export const clientPresenceSchema = z.object({
  verifiedDomain: z.string().optional().nullable(), // Added verifiedDomain
  name: z.string().optional(),
  color: z.string().optional(),
  cursor: cursorSchema.optional().nullable(),
  message: z.string().optional().nullable(),
  cursorIndex: z.number().optional(),
  spotlightColor: z.string().optional(),
});

export const userSchema = z.object({
  presence: presenceSchema,
  // verifiedDomain: z.string().optional().nullable(), // Removed from userSchema
});

export const partyMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("sync"),
    users: z.record(userSchema),
  }),
  z.object({
    type: z.literal("changes"),
    add: z.record(userSchema).optional(),
    presence: z.record(presenceSchema).optional(),
    remove: z.array(z.string()).optional(),
  }),
]);

export const clientMessageSchema = z.object({
  type: z.literal("update"),
  presence: clientPresenceSchema, // Use the schema with verifiedDomain
});

// parse incoming message (supports json and msgpack)
export function decodeMessage(message: string | ArrayBufferLike) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return typeof message === "string" ? JSON.parse(message) : decode(message);
}

// creates a msgpack message
export function encodePartyMessage(data: z.infer<typeof partyMessageSchema>) {
  return encode(partyMessageSchema.parse(data));
  //return encode(data);
}

export function encodeClientMessage(data: z.infer<typeof clientMessageSchema>) {
  return encode(clientMessageSchema.parse(data));
  //return encode(data);
}

// Helper function to merge client presence updates while preserving verifiedDomain
export function mergePresenceUpdate(
  currentUser: User,
  presenceUpdate: Presence // Changed from Omit<Presence, 'verifiedDomain'> to Presence
): User {
  return {
    ...currentUser,
    presence: {
      ...currentUser.presence,
      ...presenceUpdate,
      // No need for explicit preservation of verifiedDomain here if it's already in presenceUpdate
      // It will be merged naturally.
    },
  };
}